import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  createCashRequest,
  CreateCashRequestProps,
  getCashRequest,
  updateCashRequest,
  UpdateCashRequestProps,
} from "@/services/cash-requests.service";
import { handlePermissionError } from "@/utils/permission-check";
import { formatCurrencyInputToNumber, isEmpty } from "@/utils/query-string";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { CashRequestDetail } from "../schema/cash-requests.schema";
import { CashRequestStatus } from "@/constants/entity-status";
import { FieldValues } from "react-hook-form";
import { EstadosEfectivo } from "@/constants/enums/status/entity-status-ids";

export interface FetchCashRequestHandlerProps {
  setLoadingRequest: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  id?: string;
  fetchPermissions: () => Promise<void>;
  setRequest: Dispatch<SetStateAction<CashRequestDetail | null | undefined>>;
  showLoad: boolean;
}

export const fetchCashRequestHandler = async ({
  fetchPermissions,
  id,
  navigate,
  setLoadingRequest,
  setRequest,
  showLoad,
}: FetchCashRequestHandlerProps) => {
  try {
    if (showLoad) setLoadingRequest(true);
    const response = await getCashRequest({
      id: Number(id),
    });

    setRequest(response);

    setLoadingRequest(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudo cargar la solicitud.",
      });
    }
    throw error;
  }
};

interface CRHProps {
  setLoadingCreateCashRequest: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  watchedValues: {
    person?: any;
    account?: any;
    sucursal?: any;
    request_amount?: any;
    request_detail?: any;
    usuarioMobile?: any;
  };
  fetchPermissions: () => Promise<void>;
}

export const createRequestHandler = async ({
  navigate,
  setLoadingCreateCashRequest,
  watchedValues,
  fetchPermissions,
}: CRHProps) => {
  setLoadingCreateCashRequest(true);

  try {
    const objToSend: CreateCashRequestProps = {
      idCuentaOrigen: watchedValues?.account?.value,
      claveSucursal: watchedValues?.sucursal?.[0],
      ...(!isEmpty(watchedValues?.request_detail) && {
        mensaje: watchedValues?.request_detail,
      }),
      monto: formatCurrencyInputToNumber(watchedValues?.request_amount ?? 0),
      idUsuarioMobile: watchedValues?.usuarioMobile?.value,
    };
    await createCashRequest(objToSend);

    toaster.create({
      title: "Solicitud creada con éxito",
      type: "success",
    });
    setLoadingCreateCashRequest(false);
    navigate(AppRoutes.CASH_REQUESTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    setLoadingCreateCashRequest(false);

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.CASH_REQUESTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se ha podido crear la solicitud.",
      });
    }
    throw error;
  }
};

export interface CashRequestEditAmountProps {
  setLoadingEditAmount: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  request?: CashRequestDetail | null;
  watchedValues: FieldValues;
  errors: any;
  fetchRequest: (showLoad: boolean) => Promise<void>;
  fetchPermissions: () => Promise<void>;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
}

export const updateCashRequestHandler = async ({
  setLoadingEditAmount,
  navigate,
  request,
  watchedValues,
  errors,
  fetchRequest,
  fetchPermissions,
  setIsEditingAmount,
}: CashRequestEditAmountProps) => {
  if (request?.estado !== CashRequestStatus.NEEDS_INFO) return;
  if (errors?.request_amount) return;
  try {
    setLoadingEditAmount(true);

    await updateCashRequest({
      idSolicitud: request?.id,
      estado: EstadosEfectivo.RequiereInformacion,
      monto: formatCurrencyInputToNumber(watchedValues?.request_amount),
    });

    await fetchRequest(false);

    setLoadingEditAmount(false);
    setIsEditingAmount((prev) => !prev);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (handled) setIsEditingAmount(false);
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo editar el monto solicitado.",
      });
    }
    throw error;
  } finally {
    setLoadingEditAmount(false);
  }
};

export interface SendCashRequestInfoProps {
  setLoadingSendInfo: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  request?: CashRequestDetail | null;
  event?: FieldValues;
  fetchRequest: (showLoad: boolean) => Promise<void>;
  fetchPermissions: () => Promise<void>;
  setOpenInfoModal: Dispatch<SetStateAction<boolean>>;
}

export const cashRequestSendInfoHandler = async ({
  setLoadingSendInfo,
  navigate,
  request,
  event,
  fetchRequest,
  fetchPermissions,
  setOpenInfoModal,
}: SendCashRequestInfoProps) => {
  setLoadingSendInfo(true);
  try {
    const objToSend: UpdateCashRequestProps = {
      idSolicitud: request?.id ?? 0,
      estado: EstadosEfectivo.RequiereInformacion,
      mensaje: event?.request_detail,
      idMotivo: event?.message_motive?.[0],
    };

    await updateCashRequest(objToSend);

    toaster.create({
      type: "success",
      title: "Pedido de información realizado exitosamente",
    });

    await fetchRequest(false);
    setLoadingSendInfo(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "Hubo un error al enviar el pedido de información.",
      });
      throw error;
    }
  } finally {
    setLoadingSendInfo(false);
  }

  setOpenInfoModal(false);
};

export interface RejectCashRequestHandlerProps {
  setLoadingRejectRequest: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  request?: CashRequestDetail | null;
  event?: FieldValues;
  fetchRequest: (showLoad: boolean) => Promise<void>;
  fetchPermissions: () => Promise<void>;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
}

export const rejectCashRequestHandler = async ({
  setLoadingRejectRequest,
  navigate,
  request,
  event,
  fetchRequest,
  fetchPermissions,
  setOpenRejectModal,
}: RejectCashRequestHandlerProps) => {
  setLoadingRejectRequest(true);
  try {
    const objToSend: UpdateCashRequestProps = {
      idSolicitud: Number(request?.id),
      estado: EstadosEfectivo.Rechazada,
      mensaje: event?.request_detail,
      idMotivo: event?.message_motive?.[0],
    };

    await updateCashRequest(objToSend);

    toaster.create({
      type: "success",
      title: "Solicitud rechazada exitosamente.",
    });

    await fetchRequest(false);
    setLoadingRejectRequest(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo rechazar la solicitud.",
      });
      throw error;
    }
  } finally {
    setLoadingRejectRequest(false);
  }

  setOpenRejectModal(false);
};


export interface HandleAcceptCashRequestHandlerProps {
  setLoadingAcceptRequest: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  request?: CashRequestDetail | null;
  status: EstadosEfectivo;
  fetchRequest: (showLoad: boolean) => Promise<void>;
  fetchPermissions: () => Promise<void>;
}

export const handleAcceptRequestHander = async ({
  setLoadingAcceptRequest,
  navigate,
  request,
  status,
  fetchRequest,
  fetchPermissions,
}: HandleAcceptCashRequestHandlerProps) => {
 setLoadingAcceptRequest(true);
    try {
      const objToSend: UpdateCashRequestProps = {
        idSolicitud: request?.id ?? 0,
        estado: status,
      };

      await updateCashRequest(objToSend);

      const statusString =
        status === EstadosEfectivo.Disponible ? "ejecutada" : "finalizada";
      toaster.create({
        type: "success",
        title: `Solicitud ${statusString} exitosamente.`,
      });

      await fetchRequest(false);
    } catch (error) {
      const statusString =
        status === EstadosEfectivo.Disponible ? "ejecutar" : "finalizar";
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title: `No se pudo ${statusString} la solicitud.`,
        });
      }
      throw error;
    } finally {
      setLoadingAcceptRequest(false);
    }
}